import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CloudNavBar } from "../components/CloudNavBar";

interface APIKey {
  id: string;
  key_prefix: string;
  name: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="ml-2 px-2 py-1 text-xs bg-muted rounded hover:bg-accent transition-colors"
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

interface NewKeyResult {
  id: string;
  key: string;
  key_prefix: string;
  name: string;
  expires_at: string | null;
  created_at: string;
}

function CreateKeyForm({ onCreated }: { onCreated: (result: NewKeyResult) => void }) {
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [hasExpiry, setHasExpiry] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload: { name: string; expires_at?: string } = { name };
      if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
      const result = await api.post<NewKeyResult>("/api-keys", payload);
      onCreated(result);
      setName("");
      setExpiresAt("");
    } catch (e: any) {
      setError(e?.message || "Failed to create API key");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</p>}
      <div className="flex gap-3 flex-wrap">
        <input
          type="text"
          className="flex-1 min-w-[180px] border rounded px-3 py-2 text-sm bg-background"
          placeholder="Key name (e.g. CI/CD pipeline)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
            <input
              type="checkbox"
              checked={hasExpiry}
              onChange={(e) => {
                setHasExpiry(e.target.checked);
                if (!e.target.checked) setExpiresAt("");
              }}
            />
            Expires
          </label>
          {hasExpiry && (
            <input
              type="datetime-local"
              className="border rounded px-3 py-2 text-sm bg-background"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              required
            />
          )}
        </div>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Creating..." : "Create Key"}
        </button>
      </div>
    </form>
  );
}

function NewKeyBanner({ result, onDismiss }: { result: NewKeyResult; onDismiss: () => void }) {
  return (
    <div className="border border-green-300 bg-green-50 rounded-lg p-4 space-y-2">
      <div className="flex justify-between items-start">
        <p className="text-sm font-semibold text-green-800">
          API key created: <span className="font-normal">{result.name}</span>
        </p>
        <button onClick={onDismiss} className="text-green-600 hover:text-green-900 text-sm ml-4">
          Dismiss
        </button>
      </div>
      <p className="text-xs text-green-700">
        Copy your key now — it will not be shown again.
      </p>
      <div className="flex items-center bg-white rounded border border-green-200 px-3 py-2 font-mono text-sm break-all">
        <span className="flex-1">{result.key}</span>
        <CopyButton value={result.key} />
      </div>
    </div>
  );
}

export default function APIKeysPage() {
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newKey, setNewKey] = useState<NewKeyResult | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchKeys = () => {
    setLoading(true);
    api
      .get<{ api_keys: APIKey[] }>("/api-keys")
      .then((res) => setKeys(res.api_keys))
      .catch((e) => setError(e?.message || "Failed to load API keys"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleCreated = (result: NewKeyResult) => {
    setNewKey(result);
    // Add to list immediately
    setKeys((prev) => [
      {
        id: result.id,
        key_prefix: result.key_prefix,
        name: result.name,
        last_used_at: null,
        expires_at: result.expires_at,
        created_at: result.created_at,
      },
      ...prev,
    ]);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete API key "${name}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.del(`/api-keys/${id}`);
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (e: any) {
      alert(e?.message || "Failed to delete API key");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <div className="max-w-3xl mx-auto py-8 px-6">
      <h1 className="text-2xl font-bold mb-2">API Keys</h1>
      <p className="text-sm text-muted-foreground mb-2">
        API keys authenticate programmatic access to the API and are used by edge checker
        instances to connect to the platform. Keys are scoped to this tenant.
      </p>
      <p className="text-xs text-muted-foreground mb-6">
        API keys are used by edge probes to connect.{" "}
        <Link to="/account/edge-probes" className="underline hover:no-underline">
          Manage Edge Probes &rarr;
        </Link>
      </p>

      {/* New key banner */}
      {newKey && (
        <div className="mb-6">
          <NewKeyBanner result={newKey} onDismiss={() => setNewKey(null)} />
        </div>
      )}

      {/* Create key form */}
      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <h2 className="text-base font-semibold mb-3">Create New Key</h2>
        <CreateKeyForm onCreated={handleCreated} />
      </div>

      {/* Keys list */}
      <div className="bg-card rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-base font-semibold">Active Keys</h2>
        </div>
        {error && (
          <p className="text-sm text-red-600 p-4">{error}</p>
        )}
        {loading ? (
          <p className="text-sm text-muted-foreground p-6">Loading...</p>
        ) : keys.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6">
            No API keys yet. Create one above.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">Name</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">Prefix</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">Last Used</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">Expires</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">Created</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {keys.map((key) => (
                <tr key={key.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-3 font-medium">{key.name}</td>
                  <td className="px-6 py-3 font-mono text-muted-foreground">{key.key_prefix}…</td>
                  <td className="px-6 py-3 text-muted-foreground">{formatDate(key.last_used_at)}</td>
                  <td className="px-6 py-3 text-muted-foreground">
                    {key.expires_at ? (
                      <span className={new Date(key.expires_at) < new Date() ? "text-red-500" : ""}>
                        {formatDate(key.expires_at)}
                      </span>
                    ) : (
                      "Never"
                    )}
                  </td>
                  <td className="px-6 py-3 text-muted-foreground">{formatDate(key.created_at)}</td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => handleDelete(key.id, key.name)}
                      disabled={deletingId === key.id}
                      className="text-red-600 hover:underline text-xs disabled:opacity-50"
                    >
                      {deletingId === key.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}
